import { type NotificationModel } from '@/shared/api'
import { TABLE_PAGE_SIZE_OPTIONS } from '@/shared/lib/query'
import { TableWrapper } from '@/shared/ui'
import { Table, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import { type NotificationsSortState } from '../../lib/notifications-query-model'
import {
  $notificationsTablePage,
  $notificationsTablePageSize,
  $notificationsTableSort,
  notificationsTablePageChanged,
  notificationsTablePageReset,
  notificationsTablePageSizeChanged,
  notificationsTableQuery,
  notificationsTableSortChanged,
  notificationsTableSortReset,
} from '../../model/notifications-table.model'
import { useNotificationsTableColumns } from './useNotificationsTableColumns'

interface NotificationsTableProps {
  onEdit: (notificationId: string) => void
  onView: (notificationId: string) => void
}
export const NotificationsTable = ({
  onEdit,
  onView,
}: NotificationsTableProps) => {
  const { data, pending } = useUnit(notificationsTableQuery)

  const sort = useUnit($notificationsTableSort)
  const changeSort = useUnit(notificationsTableSortChanged)
  const resetSort = useUnit(notificationsTableSortReset)

  const page = useUnit($notificationsTablePage)
  const changePage = useUnit(notificationsTablePageChanged)
  const resetPage = useUnit(notificationsTablePageReset)
  const pageSize = useUnit($notificationsTablePageSize)
  const changePageSize = useUnit(notificationsTablePageSizeChanged)

  const tableColumns: TableProps<NotificationModel>['columns'] =
    useNotificationsTableColumns({ onEdit, onView, sort })

  if (!data?.items || !data.items.length) {
    return null
  }

  const onTableChange: TableProps<NotificationModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'
        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as NotificationsSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
      if (pagination.pageSize !== pageSize) {
        changePageSize(pagination.pageSize)
        resetPage()
      }
    }
  }

  return (
    <TableWrapper>
      <Table
        sticky
        showSorterTooltip={false}
        rowKey={(record) => record.id}
        bordered
        columns={tableColumns}
        dataSource={data.items}
        loading={pending}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data.totalItems,
          showSizeChanger: true,
          pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
          locale: { items_per_page: 'Уведомлений на странице' },
        }}
      />
    </TableWrapper>
  )
}
